import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaArrowLeft, FaClock, FaBook, FaSearch, FaList, FaBookmark } from 'react-icons/fa';
import SocialShare from '../components/SocialShare';
import VoteButtons from '../components/VoteButtons';

interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

interface Doc {
  id: string;
  title: string;
  slug: string;
  category: string;
  content: string;
  excerpt?: string;
  status?: 'draft' | 'published';
  order?: number;
  metadata?: {
    readTime?: number;
    lastModified?: string;
  };
}

const DocPage: React.FC = () => {
  const { slug } = useParams();
  const [doc, setDoc] = useState<Doc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeHeading, setActiveHeading] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [allDocs, setAllDocs] = useState<Doc[]>([]);

  const API_URL = import.meta.env.VITE_API_BACKEND_URL;
  const MAIN_SITE = import.meta.env.VITE_MAIN_SITE_URL || 'https://nostrahealth.com';

  // Extract headings from markdown content for TOC
  const tableOfContents = useMemo(() => {
    if (!doc?.content) return [];

    const headings: HeadingItem[] = [];
    const lines = doc.content.split('\n');

    lines.forEach((line) => {
      const match = line.match(/^(#{1,3})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const text = match[2].trim();
        const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        headings.push({ id, text, level });
      }
    });

    return headings;
  }, [doc?.content]);

  // Filter docs based on search term
  const filteredDocs = useMemo(() => {
    if (!searchTerm.trim()) return allDocs;

    const term = searchTerm.toLowerCase();
    return allDocs.filter(d =>
      d.title.toLowerCase().includes(term) ||
      d.category.toLowerCase().includes(term) ||
      d.excerpt?.toLowerCase().includes(term)
    );
  }, [allDocs, searchTerm]);

  useEffect(() => {
    if (slug) {
      fetchDoc();
    } else {
      setError(true);
      setLoading(false);
    }
    fetchAllDocs();
  }, [slug]);

  const fetchAllDocs = async () => {
    try {
      const res = await fetch(`${API_URL}/documentation/dev-docs`);
      const data = await res.json();
      const docsArray = Array.isArray(data) ? data : (data.data || []);
      const published = docsArray
        .filter((d: Doc) => d.status === 'published')
        .sort((a: Doc, b: Doc) => (a.order || 0) - (b.order || 0))
        .slice(0, 8); // Show top 8 docs
      setAllDocs(published);
    } catch (err) {
      console.error('Error fetching all docs:', err);
    }
  };

  // Track active heading for TOC highlighting with scroll events
  useEffect(() => {
    if (!tableOfContents.length || !doc) return;

    const handleScroll = () => {
      const headingElements = tableOfContents.map(({ id }) => {
        const element = document.getElementById(id);
        if (element) {
          const rect = element.getBoundingClientRect();
          return {
            id,
            top: rect.top,
            bottom: rect.bottom,
          };
        }
        return null;
      }).filter(Boolean);

      // Find the heading that's currently in the viewport (closest to top of screen)
      const viewportMiddle = window.innerHeight / 3; // Check upper third of viewport
      let activeId = '';

      for (let i = headingElements.length - 1; i >= 0; i--) {
        const heading = headingElements[i];
        if (heading && heading.top <= viewportMiddle) {
          activeId = heading.id;
          break;
        }
      }

      if (activeId) {
        setActiveHeading(activeId);
      }
    };

    // Initial check
    handleScroll();

    // Add scroll listener with throttle
    let ticking = false;
    const scrollListener = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollListener, { passive: true });

    return () => {
      window.removeEventListener('scroll', scrollListener);
    };
  }, [tableOfContents, doc]);

  const fetchDoc = async () => {
    try {
      setLoading(true);
      setError(false);
      const res = await fetch(`${API_URL}/documentation/dev-docs/path/${slug}`);
      const data = await res.json();
      if (data.success) {
        setDoc(data.data);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error fetching doc:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-Color"></div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white flex items-center justify-center">
        <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-12 border border-white/40 shadow-glass-hover text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Documentation Not Found</h1>
          <p className="text-gray-600 mb-8">The documentation page you're looking for doesn't exist.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-Color to-brightColor text-white px-8 py-3 rounded-full font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
          >
            <FaArrowLeft />
            Back to Documentation
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{doc.title} | NostraHealth Docs</title>
        <meta name="description" content={doc.excerpt || doc.title} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://docs.nostrahealth.com/${doc.slug}`} />
        <meta property="og:title" content={doc.title} />
        <meta property="og:description" content={doc.excerpt || doc.title} />
        <meta property="og:site_name" content="NostraHealth Documentation" />
        <meta property="article:section" content={doc.category} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://docs.nostrahealth.com/${doc.slug}`} />
        <meta name="twitter:title" content={doc.title} />
        <meta name="twitter:description" content={doc.excerpt || doc.title} />

        <link rel="canonical" href={`https://docs.nostrahealth.com/${doc.slug}`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-lightPurple via-backgroundColor to-white">
        {/* Header */}
        <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50 shadow-lg">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-Color to-brightColor bg-clip-text text-transparent hover:from-hoverColor hover:to-Color transition-all">
                NostraHealth <span className="text-sm font-normal text-gray-600">/ Docs</span>
              </Link>
              <nav className="flex items-center gap-6">
                <Link to="/" className="text-gray-700 hover:text-Color font-medium transition-colors flex items-center gap-2">
                  <FaArrowLeft size={14} />
                  All Docs
                </Link>
                <a href={MAIN_SITE} className="text-gray-700 hover:text-Color font-medium transition-colors">
                  Home
                </a>
                <a href={import.meta.env.VITE_BLOG_URL} className="text-gray-700 hover:text-Color font-medium transition-colors">
                  Blog
                </a>
              </nav>
            </div>
          </div>
        </header>

        {/* Main Layout with Sidebars */}
        <div className="max-w-[1800px] mx-auto px-4 py-12">
          <div className="flex gap-2">
            {/* Left Sidebar - Search & Navigation */}
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-6">
                {/* Search */}
                <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaSearch className="text-Color" />
                    Search
                  </h3>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search docs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 rounded-xl backdrop-blur-xl bg-white/80 border border-white/40 text-gray-900 text-sm focus:outline-none focus:border-Color/50 transition-all placeholder-gray-500"
                    />
                  </div>
                </div>

                {/* Quick Navigation */}
                <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaList className="text-Color" />
                    Quick Nav
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    <Link
                      to="/"
                      className="block px-4 py-2 rounded-xl bg-white/40 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white transition-all text-sm font-medium"
                    >
                      All Docs
                    </Link>
                    {filteredDocs.map((d) => (
                      <Link
                        key={d.id}
                        to={`/${d.slug}`}
                        className={`block px-4 py-2 rounded-xl transition-all text-sm font-medium ${
                          d.slug === slug
                            ? 'bg-gradient-to-r from-Color to-brightColor text-white'
                            : 'bg-white/40 hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white'
                        }`}
                      >
                        {d.title}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Category Card */}
                {doc && (
                  <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                    <h3 className="text-sm font-bold text-gray-600 mb-2">Category</h3>
                    <div className="px-4 py-2 bg-gradient-to-r from-Color to-brightColor text-white rounded-xl text-sm font-bold text-center">
                      {doc.category}
                    </div>
                  </div>
                )}
              </div>
            </aside>

            {/* Main Content */}
            <article className="flex-1 min-w-0">
              <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-8 md:p-10 border border-white/40 shadow-2xl">
            {/* Breadcrumb */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm backdrop-blur-xl bg-white/50 px-4 py-2 rounded-full border border-white/40 shadow-glass inline-flex">
                <Link to="/" className="text-gray-600 hover:text-Color font-medium transition-colors">Docs</Link>
                <span className="text-gray-400">/</span>
                <span className="text-Color font-bold">{doc.category}</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-5xl font-black text-gray-900 mb-6 leading-tight bg-gradient-to-r from-darkPurple to-Color bg-clip-text text-transparent">
              {doc.title}
            </h1>

            {/* Meta Info */}
            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-white/40">
              <div className="flex items-center gap-2 backdrop-blur-xl bg-white/50 px-4 py-2 rounded-full border border-white/40 shadow-glass">
                <FaBook className="text-Color" />
                <span className="text-sm font-medium text-gray-700">{doc.category}</span>
              </div>
              {doc.metadata?.readTime && (
                <div className="flex items-center gap-2 backdrop-blur-xl bg-white/50 px-4 py-2 rounded-full border border-white/40 shadow-glass">
                  <FaClock className="text-Color" />
                  <span className="text-sm font-medium text-gray-700">{doc.metadata.readTime} min read</span>
                </div>
              )}
            </div>

            {/* Excerpt */}
            {doc.excerpt && (
              <div className="text-xl text-gray-700 mb-12 pb-8 border-b border-white/40 font-medium">
                {doc.excerpt}
              </div>
            )}

            {/* Content */}
            <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children, ...props }) => {
                  const text = String(children);
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h1 id={id} {...props}>{children}</h1>;
                },
                h2: ({ children, ...props }) => {
                  const text = String(children);
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h2 id={id} {...props}>{children}</h2>;
                },
                h3: ({ children, ...props }) => {
                  const text = String(children);
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                  return <h3 id={id} {...props}>{children}</h3>;
                },
                code({ node, className, children, ...props }: any) {
                  const match = /language-(\w+)/.exec(className || '');
                  const inline = (props as any).inline;
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus as any}
                      language={match[1]}
                      PreTag="div"
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {doc.content}
            </ReactMarkdown>
          </div>

          {/* Vote and Share Section */}
          <div className="mt-12 pt-8 border-t border-white/40">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Was this documentation helpful?</h4>
                <VoteButtons docId={doc.id} />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Share this page</h4>
                <SocialShare
                  url={`https://docs.nostrahealth.com/${doc.slug}`}
                  title={doc.title}
                  description={doc.excerpt}
                  docId={doc.id}
                />
              </div>
            </div>
          </div>

          {/* Back to Docs */}
          <div className="mt-12 pt-8 border-t border-white/40">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-Color hover:text-hoverColor font-bold transition-all group"
            >
              <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
              Back to all documentation
            </Link>
          </div>
          </div>
        </article>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden xl:block w-80 flex-shrink-0">
          <div className="sticky top-24 space-y-6">
            {/* Table of Contents */}
            {tableOfContents.length > 0 && (
              <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaBookmark className="text-Color" />
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {tableOfContents.map((heading) => (
                    <a
                      key={heading.id}
                      href={`#${heading.id}`}
                      className={`block py-2 px-3 rounded-lg text-sm transition-all ${
                        activeHeading === heading.id
                          ? 'bg-gradient-to-r from-Color to-brightColor text-white font-bold shadow-lg'
                          : heading.level === 1
                          ? 'font-bold text-gray-900'
                          : heading.level === 2
                          ? 'pl-6 font-medium text-gray-700'
                          : 'pl-9 text-gray-600'
                      } hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white`}
                    >
                      {heading.text}
                    </a>
                  ))}
                </nav>
              </div>
            )}

            {/* Category Card */}
            {doc && (
              <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaBook className="text-Color" />
                  Category
                </h3>
                <div className="px-4 py-2 bg-gradient-to-r from-Color to-brightColor text-white rounded-xl text-sm font-bold text-center">
                  {doc.category}
                </div>
              </div>
            )}

            {/* Share Card */}
            <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 border border-white/40 shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Share This</h3>
              <SocialShare
                url={`https://docs.nostrahealth.com/${doc?.slug}`}
                title={doc?.title || ''}
                description={doc?.excerpt}
                docId={doc?.id}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>

        {/* CTA Section */}
        <div className="py-12">
          <div className="max-w-[1800px] mx-auto px-4">
            <div className="backdrop-blur-xl bg-white/60 rounded-3xl p-12 border border-white/40 shadow-2xl overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-r from-Color/10 via-brightColor/10 to-accentPurple/10"></div>
              <div className="relative z-10 text-center">
                <h2 className="text-4xl font-black mb-4 bg-gradient-to-r from-Color via-brightColor to-accentPurple bg-clip-text text-transparent">
                  Need More Help?
                </h2>
                <p className="text-gray-700 mb-8 max-w-2xl mx-auto text-lg">
                  Contact our support team or explore more documentation
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <a
                    href={`${MAIN_SITE}/contact`}
                    className="inline-block bg-gradient-to-r from-Color to-brightColor text-white px-10 py-4 rounded-full font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300"
                  >
                    Contact Support
                  </a>
                  <Link
                    to="/"
                    className="inline-block backdrop-blur-xl bg-white/70 border-2 border-Color text-Color px-10 py-4 rounded-full font-bold hover:bg-gradient-to-r hover:from-Color hover:to-brightColor hover:text-white hover:shadow-2xl hover:scale-105 transition-all duration-300"
                  >
                    Browse Docs
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="backdrop-blur-xl bg-darkPurple/90 text-white py-8 border-t border-white/10">
          <div className="max-w-[1800px] mx-auto px-4 text-center">
            <p className="text-gray-300">
              © {new Date().getFullYear()} NostraHealth. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default DocPage;
